import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import createProctorSession from '@utils/beexpert/proctorSession';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import { getCurrentSessionUID } from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import type { UseProctorResult } from '@beexpert.ai/proctor/react';
// Imported directly (not via ./index) - the index also exports this provider
import ProctorPermissionDialog from './PermissionDialog';
import {
  storeCredentials,
  retrieveStoredCredentials,
  getStoredQuizUID,
  clearStoredCredentials
} from './credentialsStorage';
import { setProctorReport, clearProctorReport } from './proctorReportStore';
import type {
  ProctorCredentials,
  ProctorContextValue,
  ProctorProviderProps
} from './types';

// Dynamically import ProctorSession with SSR disabled to avoid browser API errors
const ProctorSession = dynamic(() => import('./ProctorSession'), {
  ssr: false,
  loading: () => null
});

export type { ProctorCredentials, ProctorContextValue };

const ProctorContext = createContext<ProctorContextValue | null>(null);

export const useProctorContext = (): ProctorContextValue | null => {
  const context = useContext(ProctorContext);
  if (!context) {
    return null;
  }
  return context;
};

// Stable default proctorHook to prevent unnecessary re-renders
const DEFAULT_PROCTOR_HOOK = {
  start: async () => {},
  stop: async () => {},
  status: 'idle',
  on: () => () => {}
};

const TERMINAL_STATUSES = ['completed', 'stopped', 'error'];

/**
 * ProctorProvider - Manages the BeExpert proctoring session lifecycle.
 *
 * Provisioning is always consumer-driven (Footer / InstructionsPage call
 * provisionSession after the permission dialog), and sessions persist in
 * sessionStorage so a page refresh mid-test resumes the same session.
 */
export const ProctorProvider = ({
  children,
  quizUID,
  quizTitle,
  quizDuration,
  quizStartTime,
  enabled = true,
  allowSessionRestore = false,
  isLive = false
}: ProctorProviderProps) => {
  const [credentials, setCredentials] = useState<ProctorCredentials | null>(
    null
  );
  const [proctorHook, setProctorHook] = useState<UseProctorResult | null>(null);
  const [isResumedSession, setIsResumedSession] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [hasStartedSuccessfully, setHasStartedSuccessfully] = useState(false);
  const [sdkStatus, setSdkStatus] = useState('idle');

  // Recovery flow for a restored session whose auto-start failed: camera/mic
  // permission revoked while away, or a single window/tab shared instead of
  // the entire screen. The dialog renders here (not in an instructions page)
  // because mid-test resume happens on the taking page.
  const [showResumePermissionDialog, setShowResumePermissionDialog] =
    useState(false);
  const [resumeScreenShareDenied, setResumeScreenShareDenied] = useState(false);
  // Bumped when the candidate re-grants permissions - re-runs the auto-start
  // effect for another start() attempt on the SAME restored session.
  const [resumeAttempt, setResumeAttempt] = useState(0);
  // Bumped after a failed start() - remounts ProctorSession so the
  // half-started SDK instance releases any camera/mic/ws it opened.
  const [sdkInstanceKey, setSdkInstanceKey] = useState(0);

  const initializedQuizRef = useRef<string | null>(null); // quiz already provisioned this mount
  const isCreatingSessionRef = useRef(false); // concurrent-provision guard
  const hasAutoStartedRef = useRef(false); // restored session auto-start latch
  // Set by the permission:change listener when the SDK reports the candidate
  // shared a single window/tab - read in start()'s catch to seed the
  // recovery dialog at the screen step with the access-denied image.
  const resumeScreenDeniedRef = useRef(false);
  const resumePermissionUnsubRef = useRef<(() => void) | undefined>(undefined);

  // Live mirrors of credentials/status, updated synchronously alongside the
  // state. Callbacks like provisionSession are captured inside consumers'
  // event handlers (e.g. Footer holds the click-time proctorContext), so
  // their closed-over state can be stale by the time they run - notably
  // right after clearSession() in the same handler. Reading through these
  // refs always sees the truth.
  const credentialsRef = useRef<ProctorCredentials | null>(null);
  const sdkStatusRef = useRef('idle');

  const me = useMyInfo(getMe);

  const setCredentialsSynced = useCallback(
    (creds: ProctorCredentials | null) => {
      setCredentials(creds);
      credentialsRef.current = creds;
    },
    []
  );

  // Full reset: storage, state AND the dead session's hook/status. Leaving
  // hook/status in place makes the next attempt race against stale values -
  // isReady can compute from the OLD session's status, and consumers can
  // grab the OLD hook and call start() on a torn-down SDK instance. Clearing
  // credentials also unmounts ProctorSession, which is what makes the SDK
  // release its own camera/mic/ws resources (per useProctor's own cleanup-
  // on-unmount behavior) - so this must run on every session failure path,
  // not just on manual stop.
  const clearSession = useCallback(() => {
    clearStoredCredentials();
    setCredentialsSynced(null);
    setProctorHook(null);
    setSdkStatus('idle');
    sdkStatusRef.current = 'idle';
    initializedQuizRef.current = null;
    isCreatingSessionRef.current = false;
  }, [setCredentialsSynced]);

  // Provision a brand-new session from the BeExpert API
  const initializeProctor = useCallback(async () => {
    if (!enabled || !me || !me.uid) {
      throw new Error(
        'Cannot initialize: Proctoring not enabled or user not available'
      );
    }

    if (isCreatingSessionRef.current) {
      throw new Error('Session creation already in progress');
    }

    if (initializedQuizRef.current === quizUID && credentialsRef.current) {
      return credentialsRef.current;
    }

    try {
      isCreatingSessionRef.current = true;

      const candidate = {
        candidate_id: me.uid,
        candidate_name:
          `${me.first_name || ''} ${me.last_name || ''}`.trim() || me.username,
        candidate_mobile: me.phone || '',
        candidate_email: me.email || ''
      };

      const evaluation = {
        On_Id: quizUID,
        title: quizTitle || 'Assessment',
        scheduledStartAt: quizStartTime || new Date().toISOString(),
        scheduledDurationSeconds: quizDuration || 900
      };

      const session = await createProctorSession(isLive, quizUID);
      // const session = await createProctorSession(candidate, evaluation);

      // A fresh session invalidates the previous attempt's captured report
      clearProctorReport();

      storeCredentials(session, { quizUID, quizTitle, quizDuration });
      setCredentialsSynced({
        sessionId: session.sessionId,
        sessionToken: session.sessionToken,
        apiUrl: session.apiUrl,
        accessTokenTTL: session.accessTokenTTL
      });
      initializedQuizRef.current = quizUID;

      return session;
    } finally {
      isCreatingSessionRef.current = false;
    }
  }, [
    enabled,
    quizUID,
    quizTitle,
    quizDuration,
    quizStartTime,
    isLive,
    me,
    setCredentialsSynced
  ]);

  // Return the active session's credentials, or provision a fresh session.
  // Reads through the refs, never the closed-over state (see refs above).
  const provisionSession = useCallback(async () => {
    const liveCredentials = credentialsRef.current;
    const isTerminal = TERMINAL_STATUSES.includes(sdkStatusRef.current);

    if (liveCredentials && !isTerminal) {
      return liveCredentials;
    }

    if (liveCredentials && isTerminal) {
      clearStoredCredentials();
      setCredentialsSynced(null);
    }

    const session = await initializeProctor();
    if (!session || !session.sessionId) {
      throw new Error(
        'Failed to create proctoring session - no session returned'
      );
    }

    return {
      sessionId: session.sessionId,
      sessionToken: session.sessionToken,
      apiUrl: session.apiUrl
    };
  }, [setCredentialsSynced, initializeProctor]);

  // Restore a stored session on mount (page-refresh support)
  useEffect(() => {
    if (!enabled) return;

    // quizUID comes from next/router's query, which can still be undefined on
    // the first render after a hard refresh. Without this guard the mismatch
    // branch below would wipe the stored session before the real quizUID
    // arrives - the effect re-runs once it does.
    if (!quizUID) return;

    const storedCredentials = retrieveStoredCredentials();

    const shouldRestore =
      allowSessionRestore &&
      storedCredentials &&
      getStoredQuizUID() === quizUID;

    if (shouldRestore) {
      setCredentialsSynced(storedCredentials);
      setIsResumedSession(true);
      initializedQuizRef.current = quizUID;
    } else if (storedCredentials) {
      // Stale/mismatched/expired session - clear it to prevent 409 conflicts
      clearStoredCredentials();
      initializedQuizRef.current = null;
    }
  }, [enabled, quizUID, allowSessionRestore, setCredentialsSynced]);

  // Auto-start a restored session (permissions were granted before refresh)
  useEffect(() => {
    const hookStatus = proctorHook?.status || 'idle';

    if (
      !enabled ||
      !isResumedSession ||
      !proctorHook?.start ||
      hookStatus === 'running' ||
      hasAutoStartedRef.current
    ) {
      return;
    }

    hasAutoStartedRef.current = true;
    setIsStarting(true);

    // Screen share never survives a refresh - the SDK re-prompts for it
    // during start(). It fires { kind: 'screen', state: 'denied' } just
    // before rejecting start() when a single window/tab is shared instead
    // of the entire screen.
    resumeScreenDeniedRef.current = false;
    if (typeof proctorHook.on === 'function') {
      resumePermissionUnsubRef.current?.();
      resumePermissionUnsubRef.current = proctorHook.on(
        'permission:change',
        ({ kind, state }: { kind: string; state: string }) => {
          if (kind === 'screen' && state === 'denied') {
            resumeScreenDeniedRef.current = true;
          }
        }
      );
    }

    proctorHook
      .start()
      .then(() => {
        setIsStarting(false);
        setHasStartedSuccessfully(true);
      })
      .catch((error: any) => {
        setIsStarting(false);

        // 409 = session conflict, 401 = stored token superseded (the SDK
        // rotated it on a previous start). Either way the stored session is
        // permanently unusable - clear and reload for a fresh one.
        const status = error.response?.status;
        if (status === 409 || status === 401) {
          clearStoredCredentials();
          setTimeout(() => window.location.reload(), 1000);
          return;
        }

        // Recoverable in place: camera/mic permission revoked while away,
        // or a single window/tab shared instead of the entire screen.
        // Remount ProctorSession (fresh SDK instance, releases any streams
        // the failed start opened) and walk the permission dialog again
        // before retrying start() on the same session - a NEW session must
        // not be provisioned here, enroll already bound this session's id
        // to the ongoing attempt.
        setProctorHook(null);
        setSdkInstanceKey((key) => key + 1);
        setResumeScreenShareDenied(resumeScreenDeniedRef.current);
        setShowResumePermissionDialog(true);
      });
  }, [enabled, isResumedSession, proctorHook, resumeAttempt]);

  // Detach the resume permission listener on unmount
  useEffect(() => {
    return () => {
      resumePermissionUnsubRef.current?.();
      resumePermissionUnsubRef.current = undefined;
    };
  }, []);

  // Candidate re-granted permissions in the recovery dialog - retry start()
  // on the same restored session.
  const handleResumePermissionsGranted = useCallback(() => {
    setShowResumePermissionDialog(false);
    setResumeScreenShareDenied(false);
    resumeScreenDeniedRef.current = false;
    hasAutoStartedRef.current = false;
    setResumeAttempt((attempt) => attempt + 1);
  }, []);

  // Candidate cancelled the recovery dialog - the test continues without
  // proctoring (proctoring failures never block the test). Clear the session
  // so the SDK unmounts and releases camera/mic.
  const handleResumePermissionsCancelled = useCallback(() => {
    setShowResumePermissionDialog(false);
    setResumeScreenShareDenied(false);
    resumeScreenDeniedRef.current = false;
    clearSession();
  }, [clearSession]);

  // Receive the useProctor hook instance from ProctorSession
  const handleHookReady = useCallback((hook: UseProctorResult) => {
    setProctorHook(hook);
  }, []);

  // Capture the /complete report from a session's terminal event so
  // finish-time analytics can attach integrity score / violations / status.
  // Keyed by the test attempt (session UID), read through a ref: the SDK
  // listener captures this callback BEFORE enroll, when the attempt's
  // session UID doesn't exist yet.
  const currentSessionUID = useSelectorWithShallowEqual(
    getCurrentSessionUID
  ) as string | null;
  const currentSessionUIDRef = useRef<string | null>(currentSessionUID);
  currentSessionUIDRef.current = currentSessionUID;

  const handleSessionReport = useCallback((serverResponse: unknown) => {
    setProctorReport(currentSessionUIDRef.current, serverResponse);
  }, []);

  // Receive SDK status events from ProctorSession
  const handleStatusChange = useCallback((newStatus: string) => {
    setSdkStatus(newStatus);
    sdkStatusRef.current = newStatus;

    if (newStatus === 'running') {
      setHasStartedSuccessfully(true);
    }
  }, []);

  const { start, stop, on } = proctorHook || DEFAULT_PROCTOR_HOOK;

  const contextValue = useMemo<ProctorContextValue>(
    () => ({
      // SDK hook surface
      start,
      stop,
      status: sdkStatus,
      on,
      proctorHook: proctorHook || DEFAULT_PROCTOR_HOOK,

      // Session state
      credentials,
      hasCredentials: !!credentials,
      isReady: !!credentials && sdkStatus === 'ready',
      isRunning: sdkStatus === 'running',
      isResumedSession,
      isStarting,
      hasStartedSuccessfully,

      // Session management
      provisionSession,
      clearSession,
      clearCredentials: clearStoredCredentials
    }),
    [
      start,
      stop,
      sdkStatus,
      on,
      proctorHook,
      credentials,
      isResumedSession,
      isStarting,
      hasStartedSuccessfully,
      provisionSession,
      clearSession
    ]
  );

  return (
    <ProctorContext.Provider value={contextValue}>
      {credentials && (
        <ProctorSession
          key={`${credentials.sessionId}:${sdkInstanceKey}`}
          credentials={credentials}
          onHookReady={handleHookReady}
          onStatusChange={handleStatusChange}
          onSessionReport={handleSessionReport}
        />
      )}
      {showResumePermissionDialog && (
        <ProctorPermissionDialog
          onComplete={handleResumePermissionsGranted}
          onCancel={handleResumePermissionsCancelled}
          initialScreenPermissionState={
            resumeScreenShareDenied ? 'denied' : undefined
          }
        />
      )}
      {children}
    </ProctorContext.Provider>
  );
};

ProctorProvider.propTypes = {
  children: PropTypes.node.isRequired,
  quizUID: PropTypes.string,
  quizTitle: PropTypes.string,
  quizDuration: PropTypes.number,
  quizStartTime: PropTypes.string,
  enabled: PropTypes.bool,
  allowSessionRestore: PropTypes.bool,
  isLive: PropTypes.bool
};

export default ProctorProvider;
